import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { api, errorMessage } from "./api";
import { LicenseActivation, LicenseDetail } from "./types";
import { formatActivationDate as dateLabel, lastActiveHelp } from "./activationDates";

type PendingAction = { type: "disable" } | { type: "delete"; activation: LicenseActivation };

export default function LicenseLookupPanel({ csrf, initialLicenseKey = "", onOpenUser }: {
  csrf: string;
  initialLicenseKey?: string;
  onOpenUser: (email: string) => void;
}) {
  const [key, setKey] = useState(initialLicenseKey);
  const [license, setLicense] = useState<LicenseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<PendingAction | null>(null);

  const lookup = useCallback(async (licenseKey: string) => {
    const normalizedKey = licenseKey.trim().replace(/^\{(.*)\}$/, "$1");
    setLicense(null);
    setPending(null);
    setError("");
    setNotice("");
    if (!/^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(normalizedKey)) {
      setError("Enter the complete license key: 32 hexadecimal characters, usually grouped as 8-4-4-4-12.");
      return;
    }
    setLoading(true);
    try {
      setLicense(await api.lookupLicense(normalizedKey, csrf));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [csrf]);

  useEffect(() => {
    if (initialLicenseKey) {
      setKey(initialLicenseKey);
      lookup(initialLicenseKey);
    }
  }, [initialLicenseKey, lookup]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (!loading && !busy) lookup(key);
  };

  const confirm = async () => {
    if (!license || !pending || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (pending.type === "disable") {
        const updated = await api.disableLicense(license.id, csrf);
        setLicense(current => current ? { ...current, ...updated } : null);
        setNotice("License disabled.");
      } else {
        const activationId = pending.activation.id;
        await api.deleteLicenseActivation(license.id, activationId, csrf);
        setLicense(current => current ? { ...current, activations: current.activations.filter(item => item.id !== activationId) } : null);
        setNotice("License activation deleted.");
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setPending(null);
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography component="h2" variant="h5">License lookup</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>Enter a license key to view its owner and activated systems.</Typography>
      <Stack component="form" onSubmit={search} direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField id="support-license-key" label="License key" required fullWidth value={key}
          disabled={loading || busy || Boolean(pending)} onChange={event => setKey(event.target.value)}
          helperText="Paste the complete key in the format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx." />
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}
          disabled={!key.trim() || loading || busy || Boolean(pending)} sx={{ minWidth: 160 }}>Look up license</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 2 }} aria-live="assertive">{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mt: 2 }} role="status">{notice}</Alert>}
      {loading && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} aria-label="Looking up license" /></Box>}
      {license && <Box sx={{ mt: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
          <Box>
            <Typography component="h3" variant="h6">{license.product_name}</Typography>
            <Typography color="text.secondary">{license.product_id} · {license.trial_mode ? "Trial" : "Full license"}</Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>{license.id}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={license.valid ? "success" : "default"} label={license.valid ? "Enabled" : "Inactive / disabled"} />
            <Button color="error" startIcon={<BlockOutlinedIcon />} disabled={!license.valid || busy}
              onClick={() => setPending({ type: "disable" })}>Disable license</Button>
          </Stack>
        </Stack>
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="overline">License owner</Typography>
          {license.owner ? <Box>
            <Button sx={{ p: 0, overflowWrap: "anywhere", textAlign: "left" }} disabled={busy}
              onClick={() => license.owner && onOpenUser(license.owner.email)}>{license.owner.email}</Button>
            <Typography color="text.secondary">{license.owner.display_name} · {license.owner.active ? "Active account" : "Inactive account"}</Typography>
          </Box> : <Typography>Unclaimed — no user owns this license.</Typography>}
        </Paper>
        <Typography component="h3" variant="h6" sx={{ mt: 3 }}>Activated systems</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>{license.activations.length} activation{license.activations.length === 1 ? "" : "s"} · Activation limit: {license.max_licenses}</Typography>
        {license.activations.length === 0 ? <Typography>No systems are activated on this license.</Typography> : (
          <TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Last active: {lastActiveHelp}</Typography>
            <Table size="small" aria-label="License activations" sx={{ minWidth: 800 }}>
              <TableHead><TableRow><TableCell>System</TableCell><TableCell>Operating system</TableCell><TableCell>Machine ID</TableCell><TableCell>Activated</TableCell><TableCell>Last active</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
              <TableBody>{license.activations.map(activation => <TableRow key={activation.id}>
                <TableCell>{activation.machine_name || "Unnamed system"}</TableCell>
                <TableCell>{activation.machine_os || "Unknown"}</TableCell>
                <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 240 }}>{activation.machine_id || "—"}</TableCell>
                <TableCell>{dateLabel(activation.created_at)}</TableCell>
                <TableCell>{dateLabel(activation.updated_at)}</TableCell>
                <TableCell align="right"><Button color="error" size="small" startIcon={<DeleteOutlineIcon />} disabled={busy}
                  aria-label={`Delete activation ${activation.id}`} onClick={() => setPending({ type: "delete", activation })}>Delete</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>}
      <Dialog open={Boolean(pending)} onClose={() => !busy && setPending(null)} aria-labelledby="license-action-title" fullWidth maxWidth="sm">
        <DialogTitle id="license-action-title">{pending?.type === "delete" ? "Delete this activation?" : "Disable this license?"}</DialogTitle>
        <DialogContent>
          <Typography>{license?.product_name} · {license?.id}</Typography>
          {pending?.type === "delete" ? <>
            <Typography sx={{ mt: 1 }}>{pending.activation.machine_name || "Unnamed system"} · {pending.activation.machine_os} · {pending.activation.machine_id}</Typography>
            <Typography sx={{ mt: 2 }}>This removes this system’s activation and frees an activation slot. The system can activate again while the license is enabled.</Typography>
          </> : <Typography sx={{ mt: 2 }}>This disables the license. Previously issued offline activations may continue until the software checks online.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPending(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={confirm}>
            {busy ? "Working…" : pending?.type === "delete" ? "Delete activation" : "Confirm disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
